import { MouseEvent, useState } from 'react';
import useCustomContext from 'hooks/useCustomContext';
import menuItems, { MenuItem as CustomMenuItem } from 'menu-items';
import { useLocation } from 'react-router';
import { Box, Button, CardMedia, Link, Menu, MenuItem, Popover, Stack, useMediaQuery } from '@mui/material';
import { useTolgee, useTranslate } from '@tolgee/react';

function Header() {
  const { pathname } = useLocation();
  const upMd = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const { t } = useTranslate();

  const { isLightBg } = useCustomContext();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [langAnchorEl, setLangAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isLangMenuOpen = Boolean(langAnchorEl);

  const isOpen = Boolean(anchorEl);

  const tolgee = useTolgee();

  const isActive = (menuItem: CustomMenuItem) => {
    if (menuItem.children) {
      const isActiveChild = menuItem.children.some((child) => child.href === pathname);
      if (isActiveChild) return true;
    }

    return menuItem.href === pathname;
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleLangClick = (event: MouseEvent<HTMLButtonElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangClose = (lang?: 'en' | 'fr') => {
    if (lang)
      tolgee.changeLanguage(lang).then((value) => {
        console.log(tolgee.getLanguage());
      });
    setLangAnchorEl(null);
  };

  return (
    <Box
      component="nav"
      sx={(theme) => ({
        position: 'fixed',
        top: 0,
        left: 0,
        width: 1,
        py: '45px',
        px: '165px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 50,
        [theme.breakpoints.down('xl')]: {
          py: '33.5px',
          px: '120px'
        },
        [theme.breakpoints.down('lg')]: {
          py: '23.5px',
          px: '85px'
        },
        [theme.breakpoints.down('md')]: {
          py: '14.5px',
          px: '24px'
        }
      })}
    >
      <Box>
        <CardMedia
          component="img"
          src={isLightBg ? '/logo_blue.png' : '/logo.png'}
          alt="SHEKR Logo"
          sx={(theme) => ({
            width: '119px',
            height: '30.013px',
            [theme.breakpoints.down('xl')]: { width: '91.195px', height: '23px' },
            [theme.breakpoints.down('lg')]: { width: '65px', height: '17px' },
            [theme.breakpoints.down('md')]: { width: '67.405px', height: '17px' }
          })}
        />
      </Box>

      {upMd ? (
        <Stack direction="row" spacing={{ md: 3.125, lg: 4.5, xl: 6 }}>
          <Button
            onClick={handleLangClick}
            variant="text"
            sx={{
              p: 0,
              minWidth: 'unset',
              borderRadius: '50%',
              border: '2px solid white',
              overflow: 'hidden',
              width: 24,
              height: 24
            }}
          >
            <img
              src={`/assets/imgs/${tolgee.getLanguage()}.png`}
              alt="Language Flag"
              width="100%"
              height="100%"
              style={{ display: 'block', height: '100%' }}
            />
          </Button>

          <Menu anchorEl={langAnchorEl} open={isLangMenuOpen} onClose={() => handleLangClose()}>
            <MenuItem onClick={() => handleLangClose('en')}>
              <img
                src="/assets/imgs/en.png"
                alt="EN"
                width={24}
                height={16}
                style={{
                  minWidth: 'unset',
                  borderRadius: '50%',
                  border: '2px solid white',
                  overflow: 'hidden',
                  marginRight: '4px',
                  width: 24,
                  height: 24
                }}
              />
              <span>{t('English')}</span>
            </MenuItem>
            <MenuItem onClick={() => handleLangClose('fr')}>
              <img
                src="/assets/imgs/fr.png"
                alt="FR"
                width={24}
                height={16}
                style={{
                  minWidth: 'unset',
                  borderRadius: '50%',
                  border: '2px solid white',
                  overflow: 'hidden',
                  marginRight: '4px',
                  width: 24,
                  height: 24
                }}
              />
              <span>{t('French')}</span>
            </MenuItem>
          </Menu>
          {menuItems.items.map((item) => (
            <Link
              key={item.id}
              href={item.href}
              sx={(theme) => ({
                position: 'relative',
                color: isLightBg ? '#05444C' : '#FFF',
                fontSize: 20,
                lineHeight: 'normal',
                textDecoration: 'none',
                '&:hover': {
                  '& span': {
                    transform: 'scaleX(1)'
                  }
                },
                [theme.breakpoints.down('xl')]: {
                  fontSize: 18
                },
                [theme.breakpoints.down('lg')]: {
                  fontSize: 14
                }
              })}
            >
              {t(item.label)}
              <Box
                component="span"
                sx={(theme) => ({
                  position: 'absolute',
                  left: 0,
                  bottom: -10,
                  height: '4px',
                  width: 1,
                  bgcolor: isLightBg ? '#05444C' : '#FFF',
                  borderRadius: '6px',
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  ...(isActive(item) && {
                    transform: 'scaleX(1)'
                  }),
                  [theme.breakpoints.down('xl')]: {
                    bottom: -8
                  },
                  [theme.breakpoints.down('lg')]: {
                    bottom: -5,
                    height: '3px'
                  }
                })}
              ></Box>
            </Link>
          ))}
        </Stack>
      ) : (
        <Stack direction="row">
          <Button
            onClick={handleLangClick}
            variant="text"
            sx={{
              p: 0,
              minWidth: 'unset',
              borderRadius: '50%',
              border: '2px solid white',
              overflow: 'hidden',
              width: 24,
              height: 24
            }}
          >
            <img
              src={`/assets/imgs/${tolgee.getLanguage()}.png`}
              alt="Language Flag"
              width="100%"
              height="100%"
              style={{ display: 'block', height: '100%' }}
            />
          </Button>

          <Menu anchorEl={langAnchorEl} open={isLangMenuOpen} onClose={() => handleLangClose()}>
            <MenuItem onClick={() => handleLangClose('en')}>
              <img
                src="/assets/imgs/en.png"
                alt="EN"
                width={24}
                height={16}
                style={{
                  minWidth: 'unset',
                  borderRadius: '50%',
                  border: '2px solid white',
                  overflow: 'hidden',
                  width: 24,
                  height: 24
                }}
              />
            </MenuItem>
            <MenuItem onClick={() => handleLangClose('fr')}>
              <img
                src="/assets/imgs/fr.png"
                alt="FR"
                width={24}
                height={16}
                style={{
                  minWidth: 'unset',
                  borderRadius: '50%',
                  border: '2px solid white',
                  overflow: 'hidden',
                  width: 24,
                  height: 24
                }}
              />
            </MenuItem>
          </Menu>
          <Button disableRipple sx={{ minWidth: 'unset', padding: 0, '&:hover': { bgcolor: 'unset' } }} onClick={handleClick}>
            <Box sx={{ width: '22.369px', height: '17.001px', position: 'relative' }}>
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  ...(isOpen ? { top: '50%', transform: 'translateY(-50%) rotate(-45deg)', width: 1 } : { top: 0, width: '80%' }),
                  right: 0,
                  height: '3px',
                  transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: '4px',
                  bgcolor: isLightBg ? '#2E97A7' : '#FFF'
                }}
              ></Box>
              <Box
                component="span"
                sx={{
                  ...(isOpen && { display: 'none' }),
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  left: 0,
                  width: '80%',
                  height: '3px',
                  borderRadius: '4px',
                  bgcolor: isLightBg ? '#2E97A7' : '#FFF'
                }}
              ></Box>
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  ...(isOpen ? { top: '50%', transform: 'translateY(-50%) rotate(45deg)', width: 1 } : { bottom: 0, width: '80%' }),
                  right: 0,
                  height: '3px',
                  transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: '4px',
                  bgcolor: isLightBg ? '#2E97A7' : '#FFF'
                }}
              ></Box>
            </Box>
          </Button>
        </Stack>
      )}
      <Popover
        id={isOpen ? 'nav-menu' : undefined}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        slotProps={{
          paper: {
            sx: {
              width: '220px',
              borderRadius: '20px',
              bgcolor: '#151522',
              color: '#FFF',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              p: '29px'
            }
          }
        }}
        sx={{ mt: '19px' }}
      >
        {menuItems.items.map((item, index, self) => (
          <Link
            key={item.id}
            href={item.href}
            onClick={handlePopoverClose}
            sx={{
              display: 'block',
              position: 'relative',
              color: '#FFF',
              fontSize: 16,
              lineHeight: '22px',
              letterSpacing: '0.5px',
              textDecoration: 'none',
              ...(index < self.length - 1 && { mb: 3 }),
              '&:hover': {
                '& span': {
                  transform: 'scaleX(1)'
                }
              }
            }}
          >
            {t(item.label)}
            <Box
              component="span"
              sx={{
                position: 'absolute',
                left: 0,
                bottom: -3,
                height: '3px',
                width: 1,
                bgcolor: '#FFF',
                borderRadius: '6px',
                transform: 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                ...(isActive(item) && {
                  transform: 'scaleX(1)'
                })
              }}
            ></Box>
          </Link>
        ))}
      </Popover>
    </Box>
  );
}

export default Header;
